import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Lock, ShieldCheck } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Wordmark } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { api, googleStartUrl, managedSignInUrl } from "@/lib/api";
import { CONTACT_URL } from "@/lib/brand";
import { safeNext } from "@/lib/nav";

const GoogleGlyph = () => (
  <svg viewBox="0 0 48 48" className="h-4 w-4" aria-hidden="true">
    <path
      fill="#EA4335"
      d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.5 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6.1C12.3 13.3 17.6 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.5 24.5c0-1.6-.1-2.8-.4-4.1H24v8.4h12.7c-.3 2.1-1.6 5.2-4.7 7.3l7.6 5.9c4.5-4.2 6.9-10.3 6.9-17.5z"
    />
    <path
      fill="#FBBC05"
      d="M10.4 28.7A14.6 14.6 0 019.6 24c0-1.6.3-3.2.8-4.7l-7.8-6.1A24 24 0 000 24c0 3.9.9 7.5 2.6 10.8l7.8-6.1z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.5 0 11.9-2.1 15.6-5.9l-7.6-5.9c-2 1.4-4.8 2.4-8 2.4-6.4 0-11.7-3.8-13.6-9.8l-7.8 6.1C6.5 42.6 14.6 48 24 48z"
    />
  </svg>
);

export default function Login() {
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const { status, agencyName } = useAuth();
  const [config, setConfig] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState(params.get("error") || "");

  useEffect(() => {
    api
      .get("/auth/config")
      .then(({ data }) => setConfig(data))
      .catch(() => setConfig({ auth_mode: "none" }));
  }, []);

  useEffect(() => {
    if (status === "authenticated") window.location.replace(next);
  }, [status, next]);

  const start = () => {
    const mode = config?.auth_mode;
    if (mode !== "managed" && mode !== "google") {
      setError(
        "Sign-in has not been switched on yet. Please contact Review Presence.",
      );
      return;
    }
    setRedirecting(true);
    // One button, whichever route is live. `google` is Review Presence's own Google client and
    // is what people see; `managed` is the dormant recovery path and is never advertised here.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href =
      mode === "google" ? googleStartUrl(next) : managedSignInUrl(next);
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.05fr_1fr]">
      <div className="rp-brand-panel rp-noise relative hidden flex-col justify-between overflow-hidden p-10 text-white lg:flex">
        <Wordmark size="xl" />
        <div className="relative max-w-md">
          <h1 className="text-4xl font-semibold leading-tight tracking-tight">
            Your words, on your website.
          </h1>
          <p className="mt-4 text-base leading-7 text-white/75">
            Edit the copy on your site, see exactly what will change, and publish when you are ready.
            Nothing goes live until you say so &mdash; and every version is kept.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-white/85">
            {[
              "Drafts save as you type. Typing is not publishing.",
              "Review every change side by side before it goes live.",
              "Roll back to any earlier version at any time.",
            ].map((line) => (
              <li key={line} className="flex items-start gap-2.5">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--rp-blue-hi))]" />
                {line}
              </li>
            ))}
          </ul>
        </div>
        <p className="relative text-xs text-white/50">
          {agencyName} - private content editor
        </p>
      </div>

      <div className="flex items-center justify-center bg-[hsl(var(--background))] px-4 py-12 sm:px-8">
        <div className="w-full max-w-md">
          <div className="mb-6 lg:hidden">
            <div className="rp-brand-panel rp-noise relative overflow-hidden rounded-xl p-5">
              <Wordmark size="md" />
            </div>
          </div>

          <div className="rp-surface p-6 sm:p-8">
            <span className="grid h-10 w-10 place-items-center rounded-lg bg-[hsl(var(--muted))] text-[hsl(var(--rp-navy))]">
              <Lock className="h-5 w-5" aria-hidden="true" />
            </span>
            <h2 className="mt-4 text-2xl font-semibold tracking-tight">Sign in to your editor</h2>
            <p className="mt-2 text-sm leading-6 text-[hsl(var(--muted-foreground))]">
              Only invited email addresses can get in. Sign in with the Google account your agency
              invited.
            </p>

            {error ? (
              <Alert variant="destructive" className="mt-5" data-testid="login-error-alert">
                <AlertTitle>Could not sign you in</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}

            <Button
              onClick={start}
              disabled={redirecting || config === null}
              className="mt-6 w-full gap-2 bg-[hsl(var(--rp-navy))] hover:bg-[hsl(var(--rp-navy-2))]"
              size="lg"
              data-testid="google-signin-button"
            >
              {redirecting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <span className="grid h-5 w-5 place-items-center rounded-full bg-white">
                  <GoogleGlyph />
                </span>
              )}
              {redirecting ? "Taking you to Google\u2026" : "Continue with Google"}
            </Button>

            <div className="mt-6 flex items-start gap-2 rounded-lg bg-[hsl(var(--muted))] p-3 text-xs leading-5 text-[hsl(var(--muted-foreground))]">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>
                Anyone can reach this screen, but only invited addresses are let through. A sign-in
                from an address we do not recognise creates no account and no session.
              </span>
            </div>

            <p className="mt-6 text-xs text-[hsl(var(--muted-foreground))]">
              Need access?{" "}
              <a
                href={CONTACT_URL}
                target="_blank"
                rel="noreferrer noopener"
                className="rp-focus rounded underline hover:text-[hsl(var(--rp-blue-2))]"
                data-testid="need-access-link"
              >
                Contact Review Presence
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
